import React, { useEffect, useState } from 'react';
import { Check, Close, Edit } from '@mui/icons-material';
import { Box, IconButton, Stack, TextField, Tooltip } from '@mui/material';

import { theme } from '../../theme/theme';
import { BaseComponent } from '..';

export interface InlineTextEditComponent extends BaseComponent {
  type: 'inlineTextEdit';
  props: InlineTextEditProps;
}

export interface InlineTextEditTooltipTextProps {
  edit?: string;
  save?: string;
  cancel?: string;
}

export interface InlineTextEditProps {
  text: string;
  editMode: boolean;
  onStartEdit: Function;
  onCancel: Function;
  onSave: (updatedText: string) => void;
  maxTextLength?: number;
  fontSize?: string;
  disabled?: boolean;
  tooltipTexts?: InlineTextEditTooltipTextProps;
}

export const InlineTextEdit: React.FC<InlineTextEditProps> = ({
  text: originalText,
  editMode,
  onStartEdit,
  onCancel,
  onSave,
  maxTextLength = 300,
  fontSize = theme.typography.body2.fontSize,
  disabled = false,
  tooltipTexts = { edit: '', save: '', cancel: '' },
}) => {
  const [updatedText, setUpdatedText] = useState(originalText);

  useEffect(() => {
    setUpdatedText(originalText);
  }, [originalText]);

  return (
    <>
      {!editMode ? (
        <Stack
          direction="row"
          justifyContent="space-between"
          width="100%"
          alignItems="center"
        >
          <>
            <Box fontSize={fontSize}>{updatedText}</Box>
            <Box typography="inherit">
              <Tooltip
                title={disabled ? '' : tooltipTexts.edit ?? ''}
                arrow
                placement="top"
              >
                <span>
                  <IconButton
                    disabled={disabled}
                    onClick={() => {
                      onStartEdit();
                    }}
                  >
                    <Edit color={disabled ? 'disabled' : 'secondary'} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </>
        </Stack>
      ) : (
        <Box width="100%" height="100%" display="flex" alignItems="center">
          <Box flexGrow={1}>
            <TextField
              inputProps={{
                maxLength: maxTextLength,
                style: {
                  fontSize: fontSize,
                },
              }}
              variant="standard"
              multiline
              value={updatedText}
              fullWidth
              onChange={(
                event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
              ) => {
                setUpdatedText(event.target.value);
              }}
            />
          </Box>
          <Box>
            <Tooltip title={tooltipTexts.save ?? ''} arrow placement="top">
              <IconButton
                onClick={() => {
                  onSave(updatedText);
                }}
              >
                <Check />
              </IconButton>
            </Tooltip>
            <Tooltip title={tooltipTexts.cancel ?? ''} arrow placement="top">
              <IconButton
                onClick={() => {
                  setUpdatedText(originalText);
                  onCancel();
                }}
              >
                <Close color="action" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </>
  );
};
